import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Heart, ArrowLeft, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { useTranslation } from '@/contexts/TranslationContext';

const Auth = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showOTPStep, setShowOTPStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const { signIn, signUp, signInWithGoogle, resetPassword, signInWithOTP, verifyOTP, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Accedi o Registrati | AstroPet - Social Network per Animali';
    
    // SEO meta tags
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Accedi o registrati su AstroPet, il social network del futuro per animali. Connetti con altri proprietari, condividi momenti speciali del tuo pet.');
    }
    
    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${window.location.origin}/auth`);
    if (!canonical.parentElement) document.head.appendChild(canonical);
    
    if (user) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Invia OTP all'email inserita come parte del flusso di login
    const { error } = await signInWithOTP(email);
    if (!error) {
      setLoginEmail(email);
      setShowOTPStep(true);
      // opzionale: pulizia campi
      // setEmail('');
      setPassword('');
      toast({
        title: "Codice OTP inviato",
        description: "Controlla la tua email e inserisci il codice per completare l'accesso.",
      });
    }

    setLoading(false);
  };

  const handleOTPVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast({
        title: "Codice incompleto",
        description: "Inserisci il codice a 6 cifre completo.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const { error } = await verifyOTP(loginEmail, otpCode);
    if (!error) {
      navigate('/app');
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signUp(email, password, username);
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    await signInWithGoogle();
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await resetPassword(resetEmail);
    if (!error) {
      toast({
        title: "Email inviata",
        description: "Controlla la tua email per il link di reset password.",
      });
      setShowForgotPassword(false);
      setResetEmail('');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen gradient-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <Link 
            to="/" 
            className="absolute top-4 left-4 p-2 glass rounded-full hover:bg-white/10 transition-smooth"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 rounded-full flex items-center justify-center">
              <img src="/lovable-uploads/5e002d38-22aa-49af-85ae-34109191ea66.png" alt="AstroPet Logo" className="w-14 h-14" />
            </div>
            <h1 className="text-2xl font-bold gradient-text font-space">AstroPet</h1>
          </div>
        </div>

        <Card className="glass border-white/20">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-xl">Benvenuto nel futuro dei pet</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 glass">
                <TabsTrigger value="login" className="data-[state=active]:bg-primary">
                  Accedi
                </TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-primary">
                  Registrati
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4">
                {showOTPStep ? (
                  // Step 2: Verifica OTP
                  <div className="space-y-4">
                    <div className="text-center">
                      <h3 className="text-white text-lg font-semibold mb-2">Inserisci il codice OTP</h3>
                      <p className="text-muted-foreground text-sm">
                        Abbiamo inviato un codice a 6 cifre a<br />
                        <span className="text-primary">{loginEmail}</span>
                      </p>
                    </div>
                    
                    <form onSubmit={handleOTPVerification} className="space-y-4">
                      <div className="flex justify-center">
                        <InputOTP 
                          maxLength={6} 
                          value={otpCode} 
                          onChange={setOtpCode}
                          className="text-center"
                        >
                          <InputOTPGroup className="gap-2">
                            <InputOTPSlot index={0} className="glass border-white/20 text-white text-lg h-12 w-12" />
                            <InputOTPSlot index={1} className="glass border-white/20 text-white text-lg h-12 w-12" />
                            <InputOTPSlot index={2} className="glass border-white/20 text-white text-lg h-12 w-12" />
                          </InputOTPGroup>
                          <InputOTPSeparator />
                          <InputOTPGroup className="gap-2">
                            <InputOTPSlot index={3} className="glass border-white/20 text-white text-lg h-12 w-12" />
                            <InputOTPSlot index={4} className="glass border-white/20 text-white text-lg h-12 w-12" />
                            <InputOTPSlot index={5} className="glass border-white/20 text-white text-lg h-12 w-12" />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      
                      <HeroButton 
                        type="submit" 
                        className="w-full" 
                        disabled={loading || otpCode.length !== 6}
                      >
                        {loading ? "Verifica..." : "Verifica Codice"}
                      </HeroButton>
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full glass border-white/20 text-white hover:bg-white/10"
                        onClick={() => {
                          setShowOTPStep(false);
                          setOtpCode('');
                          setLoginEmail('');
                        }}
                      >
                        Torna al Login
                      </Button>
                    </form>
                  </div>
                ) : (
                  // Step 1: Login normale
                  <>
                    {/* Google Sign In Button */}
                    <Button 
                      type="button"
                      variant="outline" 
                      className="w-full glass border-white/20 text-white hover:bg-white/10"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {loading ? "Accesso..." : "Continua con Google"}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full border-white/20" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background/20 px-2 text-muted-foreground backdrop-blur-sm">
                          oppure
                        </span>
                      </div>
                    </div>

                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">{t('auth.email')}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            placeholder="nome@esempio.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="pl-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-white">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="pl-10 pr-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-primary hover:underline"
                        >
                          Password dimenticata?
                        </button>
                      </div>
                      <HeroButton 
                        type="submit" 
                        className="w-full" 
                        disabled={loading}
                      >
                        {loading ? "Accesso..." : "Accedi"}
                      </HeroButton>
                    </form>
                  </>
                )}
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                {/* Google Sign In Button */}
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full glass border-white/20 text-white hover:bg-white/10"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loading ? "Registrazione..." : "Registrati con Google"}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full border-white/20" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background/20 px-2 text-muted-foreground backdrop-blur-sm">
                      oppure
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-white">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="pl-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-white">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="nome@esempio.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-white">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-password"
                        type={showRegPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword(!showRegPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                      >
                        {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <HeroButton 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                  >
                    {loading ? "Registrazione..." : "Registrati"}
                  </HeroButton>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Registrandoti accetti i nostri{' '}
          <Link to="/terms-of-service" className="text-primary hover:underline">
            Termini di Servizio
          </Link>{' '}
          e la{' '}
          <Link to="/privacy-policy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>
      </div>

      {/* Reset Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md glass border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-center">Reset Password</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="nome@esempio.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10 glass border-white/20 text-white placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 glass border-white/20 text-white hover:bg-white/10"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                    }}
                  >
                    Annulla
                  </Button>
                  <HeroButton
                    type="submit"
                    className="flex-1"
                    disabled={loading}
                  >
                    {loading ? "Invio..." : "Invia"}
                  </HeroButton>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Auth;